import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { ResourceLoading } from "./ResourceLoading";
import { TWclassNames } from "./Div";
import { Link, useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

const PMDialogComponent = (props) => {
  const {
    isLoading = false,
    title,
    children = "Content missing",
    onClose = () => {},
    open = false,
    size = "2xl",
    headerActions,
    closeOnOutsideClick,
    zIndex,
    /** Tighter padding, mobile-friendly full height, flex body for embedded panels (e.g. product selector) */
    dense = false,
    /** Extra classes merged onto `Dialog.Panel` (e.g. wider max-width on desktop) */
    panelClassName = "",
  } = props;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: zIndex ?? 2000 }}
          onClose={closeOnOutsideClick ? onClose : () => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className={dense ? "fixed inset-0 z-10 overflow-y-auto" : "fixed inset-0 z-10 overflow-hidden"}>
            <div
              className={
                dense
                  ? "flex min-h-full items-center justify-center text-center p-0 sm:p-4"
                  : "flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0 py-10"
              }
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={
                    dense
                      ? TWclassNames(
                          "w-full max-w-full md:max-w-6xl lg:max-w-7xl transform rounded-none sm:rounded-2xl bg-white p-0 sm:p-4 text-left align-middle shadow-xl transition-all",
                          "flex flex-col overflow-hidden h-auto max-h-[calc(100dvh-1rem)] sm:h-[720px] sm:max-h-[80vh] mb-0 mt-0 sm:mb-[5vh] sm:mt-[5vh]",
                          panelClassName
                        )
                      : TWclassNames(
                          "lg:" + sizeClasses[size],
                          sizeClasses[size],
                          "w-full min-w-0 transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all",
                          "mb-[5vh] mt-[5vh] max-h-[85vh] h-auto min-h-0 flex flex-col overflow-hidden",
                          panelClassName
                        )
                  }
                >
                  <div
                    className={
                      dense
                        ? "px-2 pt-2 pb-2 sm:px-0 sm:pt-0 sm:pb-2 flex-shrink-0"
                        : "mt-3 sm:mt-5 mb-10 flex-shrink-0"
                    }
                  >
                    {title && (
                      <Dialog.Title
                        as="h3"
                        className="flex justify-between items-center text-lg font-medium leading-6 text-gray-900"
                      >
                        <div className="">{t(title)}</div>
                        <div className="flex relative right-0 text-right">
                          {headerActions
                            ?.filter((action) => !action.hidden)
                            .map((action) => {
                              if (action?.component) {
                                return action.component;
                              } else {
                                if (action.path) {
                                  return (
                                    <Link
                                      key={action.title}
                                      to={action.path}
                                      type="button"
                                      className="mr-2 inline-flex items-center rounded-md border border-transparent bg-blue-500 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    >
                                      <action.icon
                                        className="-ml-0.5 mr-2 h-4 w-4"
                                        aria-hidden="true"
                                      />
                                      {action.label}
                                    </Link>
                                  );
                                } else {
                                  return (
                                    <button
                                      key={action.title}
                                      onClick={action.action}
                                      type="button"
                                      className="mr-2 inline-flex items-center rounded-md border border-transparent bg-blue-500 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    >
                                      <action.icon
                                        className="-ml-0.5 mr-2 h-4 w-4"
                                        aria-hidden="true"
                                      />
                                      {action.label}
                                    </button>
                                  );
                                }
                              }
                            })}
                          {/* Close button  */}
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent px-2 py-2 bg-white text-sm font-medium text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            onClick={onClose}
                          >
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </div>
                      </Dialog.Title>
                    )}
                  </div>
                  <div
                    className={
                      dense
                        ? "flex-1 min-h-0 overflow-hidden flex flex-col"
                        : "mt-5 flex min-h-0 max-h-[calc(85vh-12rem)] min-w-0 flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
                    }
                  >
                    {isLoading ? <ResourceLoading /> : children}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders
// export const PMDialog = React.memo(
//   PMDialogComponent,
//   (prevProps, nextProps) => {
//     // Only re-render if essential props change
//     return (
//       prevProps.open === nextProps.open &&
//       prevProps.title === nextProps.title &&
//       prevProps.isLoading === nextProps.isLoading &&
//       prevProps.size === nextProps.size &&
//       prevProps.closeOnOutsideClick === nextProps.closeOnOutsideClick
//     );
//   }
// );

export const PMDialog = PMDialogComponent;

PMDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  dense: PropTypes.bool,
  size: PropTypes.oneOf([
    "sm",
    "md",
    "lg",
    "xl",
    "2xl",
    "3xl",
    "4xl",
    "5xl",
    "6xl",
    "7xl",
  ]),
  zIndex: PropTypes.number,
  panelClassName: PropTypes.string,
};
